import { Type, Static, TSchema, TObject, type TProperties } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';
import { createTableName } from '@ayocore/platform/server/utils/table-names';
import { createValidationError } from './error-handling';
import { registerPrivateFieldNames } from './private-fields-registry';
import {
  IntegerField,
  TimestampField,
  UuidField,
  DecimalField,
  type FieldMeta,
} from './field-types';

// Export all AYA field types
export {
  StringField,
  TextField,
  IntegerField,
  BigIntField,
  DecimalField,
  TimestampField,
  DateField,
  BooleanField,
  JsonField,
  ArrayField,
  UuidField,
  EnumField,
  SlugField,
  TimeField,
  BoxField,
  PointField,
} from './field-types';

/**
 * тип атрибутов схемы tobject и мета-атрибуты поля
 */
export type FieldProperty = TSchema & Partial<FieldMeta> & { anyOf?: readonly TSchema[] };

/**
 * Набор пропертей объектной схемы с метаданными AYA-полей.
 * Каждое поле — это TypeBox-схема, обогащённая Partial<FieldMeta>.
 */
export type FieldProperties = Record<string, FieldProperty>;

/**
 * Объектная схема (TObject) с AYA-метаданными для всех пропертей.
 */
export type FieldPropertiesSchema = TObject<FieldProperties>;

// Centralized Foreign Key actions and schema
export const ForeignKeyActionValues = ['CASCADE', 'SET NULL', 'RESTRICT'] as const;
export const ForeignKeyActionSchema = Type.Union(ForeignKeyActionValues.map(v => Type.Literal(v)));
export type ForeignKeyAction = (typeof ForeignKeyActionValues)[number];

export const ForeignKeySchema = Type.Object({
  column: Type.String({ description: 'Column name for foreign key' }),
  references: Type.Object({
    table: Type.String({ description: 'Referenced table name' }),
    column: Type.String({ description: 'Referenced column name' }),
  }),
  onDelete: Type.Optional(ForeignKeyActionSchema),
  onUpdate: Type.Optional(ForeignKeyActionSchema),
});
export type ForeignKey = Static<typeof ForeignKeySchema>;

export type RelationOneOptions = {
  nullable?: boolean;
  onDelete?: ForeignKeyAction;
  onUpdate?: ForeignKeyAction;
  description?: string;
};

export type RelationOneMeta = {
  $isRelation: true;
  $targetSchema: { tableName: string; privateFields?: string[] };
  $dbType: TSchema;
  $nullable: boolean;
  $onDelete: ForeignKeyAction;
  $onUpdate: ForeignKeyAction;
  title?: string;
  description?: string;
};
/**
 * DBIndex — описание индекса БД (одиночного или составного).
 */
export type DBIndex = {
  /** Колонки индекса (порядок важен) */
  columns: string[];
  /** Тип индекса (метод), по умолчанию 'btree' */
  type?: 'btree' | 'gin' | 'gist' | 'hash' | 'brin';
  /** Флаг уникальности индекса, по умолчанию false */
  unique?: boolean;
  /** Явное имя индекса (если стандартное имя превышает 63 символа) */
  name?: string;
  /**
   * Covering-колонки (INCLUDE): хранятся в листьях индекса как полезная нагрузка,
   * в поиске/сортировке не участвуют. Позволяют index-only scan — запрос отвечает
   * из индекса без обращения к heap. Поддерживается только для btree и gist.
   */
  include?: string[];
  /**
   * Условие partial-индекса (WHERE) — SQL-выражение, как в defaultRaw пишется
   * разработчиком в схеме. Индексируются только строки, удовлетворяющие условию.
   * Канонический кейс — уникальность среди «живых» записей при softDelete:
   * `{ columns: ['email'], unique: true, where: 'deleted_at IS NULL' }`.
   * ВАЖНО: смена условия при неизменном имени индекса синком не обнаруживается
   * (дифф по имени) — меняешь where → меняй name.
   */
  where?: string;
};

/**
 * Хелпер для создания DBIndex c дефолтами.
 */
export function createDBIndex(
  columns: string[],
  type: DBIndex['type'] = 'btree',
  unique: boolean = false
): DBIndex {
  return { columns, type, unique };
}

// Strongly-typed RelationOne metadata and options (moved to top section)

export type FieldSchema<T extends TSchema = TSchema> = T & Partial<FieldMeta>;
export type RelationFieldSchema = (TSchema & RelationOneMeta) & Partial<FieldMeta>;
export type AttributeSchema = FieldSchema | RelationFieldSchema;
export type PropertiesMap = Record<string, AttributeSchema>;

/**
 * Helper function to create nullable types (accepts both the type and null)
 * This handles PostgreSQL's null values for optional fields
 * IMPORTANT: Preserves $fieldType from field types
 */
export function Nullable<T extends TSchema>(schema: T) {
  const union = Type.Union([schema, Type.Null()]);

  // Preserve $fieldType and other metadata from the original schema
  const metadata = schema as Partial<FieldMeta>;
  if (metadata.$fieldType !== undefined) {
    (union as Record<string, unknown>).$fieldType = metadata.$fieldType;
    // Copy other field-specific metadata
    if (metadata.$maxLength !== undefined)
      (union as Record<string, unknown>).$maxLength = metadata.$maxLength;
    if (metadata.$precision !== undefined)
      (union as Record<string, unknown>).$precision = metadata.$precision;
    if (metadata.$scale !== undefined) (union as Record<string, unknown>).$scale = metadata.$scale;
  }

  return union;
}

/**
 * Хелпер для объявления однообъектной связи (relation one).
 *
 * - Используется только для TypeScript-типов: колонку в БД не создаёт.
 * - Возвращает опциональный тип и допускает `null`, т.к. связанный объект
 *   может не быть загружен (без join) или отсутствовать (NULL в FK).
 *
 * Итоговый тип поля: Target | null | undefined.
 *
 * Примечание: намеренно не указываем явный тип возвращаемого значения `TSchema`,
 * чтобы сохранить корректный `Static<T>` и избежать стирания типов, из-за которого
 * потребители `Static<typeof Schema>` видят `unknown` для полей RelationOne.
 */
export function RelationOne<T extends TObject<PropertiesMap>>(
  schemaInput: ExtendedSchemaDefinition<T>
) {
  const runtimeSchema = Nullable(schemaInput.schema);
  // RelationOne — виртуальное поле, всегда optional (не нужно передавать при создании)
  return Type.Optional(Type.Unsafe<Static<T> | null | undefined>({ ...runtimeSchema }));
}

/**
 * Helper function to define foreign key fields
 * Creates IntegerField with FK constraints metadata
 * Automatically infers relation object name from field name (removes _id suffix)
 */
export function RelationField<TNullable extends boolean | undefined = undefined>(
  targetSchema: ExtendedSchemaDefinition,
  title?: string,
  options: RelationOneOptions & {
    relationName?: string;
    nullable?: TNullable;
  } = {} as RelationOneOptions & { relationName?: string; nullable?: TNullable }
) {
  const schema = IntegerField<TNullable>({
    title,
    description: options.description,
    nullable: options.nullable as TNullable,
  });

  // Add relation metadata
  schema.$isRelation = true;
  schema.$foreignKey = {
    table: targetSchema.tableName,
    column: 'id',
    onDelete: options.onDelete || 'RESTRICT',
    onUpdate: options.onUpdate || 'CASCADE',
  };
  schema.$nullable = !!options.nullable;
  schema.$relationName = options.relationName; // Optional explicit name

  // Только tableName + имена приватных полей — полный объект вызывал рекурсивное раздувание meta().
  // privateFields вычисляются здесь (в момент объявления связи), чтобы withRelation мог
  // исключить секреты целевой таблицы из relation-JSON без доступа к её полной схеме.
  const privateFields = Object.entries(targetSchema.schema.properties ?? {})
    .filter(([, fieldDef]) => (fieldDef as Partial<FieldMeta>).$private === true)
    .map(([fieldName]) => fieldName);
  schema.$targetSchema = {
    tableName: targetSchema.tableName,
    ...(privateFields.length > 0 ? { privateFields } : {}),
  };

  return schema;
}

// Import field types for SystemFields (already imported at top for types)

/**
 * Standard system fields that every table should have
 *
 * These fields are automatically included in all database tables
 * and provide consistent audit trail and primary key functionality.
 */
export const SystemFields = {
  id: IntegerField({ description: 'Primary key' }),
  uuid: UuidField({
    unique: true,
    description: 'Universal unique identifier for cross-table linking',
  }),
  created_at: TimestampField({
    description: 'When the record was created',
    defaultRaw: 'CURRENT_TIMESTAMP',
  }),
  updated_at: TimestampField({
    description: 'When the record was last updated',
    defaultRaw: 'CURRENT_TIMESTAMP',
  }),
  sort_order: DecimalField({
    nullable: true,
    minimum: 0,
    precision: 20,
    scale: 10,
    description: 'Order for sorting records',
  }),
  created_by_id: IntegerField({
    nullable: true,
    description: 'ID пользователя, создавшего запись',
  }),
  updated_by_id: IntegerField({
    nullable: true,
    description: 'ID пользователя, последним обновившего запись',
  }),
  deleted_at: TimestampField({
    nullable: true,
    description: 'Дата мягкого удаления. NOT NULL = запись удалена.',
  }),
  deleted_by_id: IntegerField({
    nullable: true,
    description: 'ID пользователя, удалившего запись.',
  }),
};

/**
 * Get array of system field names
 *
 * Centralized method to get system field names for consistent usage
 * across the platform (repositories, table creation, sync operations, etc.)
 */
export function getSystemFields(): string[] {
  return Object.keys(SystemFields);
}

/**
 * TypeBox schema that validates TypeBox TObject schemas
 *
 * This is a more pragmatic approach - we validate the essential structure
 * but allow TypeBox's internal properties to pass through.
 */
export const TypeBoxObjectSchema = Type.Object(
  {
    // Core TypeBox internal properties for TObject
    type: Type.Literal('object', {
      description: 'TypeBox type identifier - must be "object" for TObject',
    }),

    properties: Type.Record(Type.RegExp(/^[a-z][a-z0-9_]*$/), Type.Any(), {
      description: 'Object properties defined in the TypeBox schema',
    }),
  },
  {
    $id: 'TypeBoxObjectSchema',
    description: 'Validates that an object is a proper TypeBox TObject schema',
    additionalProperties: true, // Allow all TypeBox internal properties (static, [Kind], params, etc.)
  }
);

/**
 * TypeBox Schema for SchemaDefinition structure
 *
 * Provides comprehensive runtime validation for schema definitions with enhanced metadata
 * for table creation, validation, and auto-registration. Uses specific TypeBox validation
 * instead of generic object validation + manual checks.
 */
export const SchemaDefinitionSchema = Type.Object(
  {
    /**
     * Имя модуля для авто-префикса tableName.
     * Передаётся явно через `import { name as moduleName } from '../module.json'`
     * — детерминированный источник, работает в любом окружении (dev/tsx/prod-bundle).
     * Если задано — `defineSchema` использует его и не лезет в stack-trace.
     * Stack-detect остаётся как fallback для случаев когда поле не передано.
     */
    moduleName: Type.Optional(
      Type.String({
        pattern: '^[a-z][a-z0-9_-]*$',
        description: 'Имя модуля (из module.json) для префиксования tableName',
      })
    ),

    /** The TypeBox schema definition - validated with specific TypeBox structure */
    schema: TypeBoxObjectSchema,

    //TODO: add here the createSchema/updateSchema/baseSchema

    /** Table name for database creation */
    tableName: Type.String({
      pattern: '^[a-z][a-z0-9_]*$',
      description: 'Database table name (lowercase with underscores)',
    }),

    /** Additional indexes to create (single-column or composite) */
    additionalIndexes: Type.Optional(
      Type.Array(
        Type.Union([
          // Single-column index by column name
          Type.String({ description: 'Single-column index by column name' }),
          // Composite/advanced index definition
          Type.Object(
            {
              columns: Type.Array(Type.String({ minLength: 1 }), {
                minItems: 1,
                description: 'Index columns (order matters)',
              }),
              type: Type.Optional(
                Type.Union(
                  [
                    Type.Literal('btree'),
                    Type.Literal('gin'),
                    Type.Literal('gist'),
                    Type.Literal('hash'),
                    Type.Literal('brin'),
                  ],
                  { description: 'Index method' }
                )
              ),
              unique: Type.Optional(Type.Boolean({ description: 'Unique index flag' })),
              name: Type.Optional(
                Type.String({
                  pattern: '^[a-z][a-z0-9_]*$',
                  maxLength: 63,
                  description:
                    'Explicit index name (required if auto-generated name exceeds 63 characters)',
                })
              ),
              include: Type.Optional(
                Type.Array(Type.String({ minLength: 1 }), {
                  minItems: 1,
                  description:
                    'Covering columns (INCLUDE) — stored in index leaves for index-only scans; btree/gist only',
                })
              ),
              where: Type.Optional(
                Type.String({
                  minLength: 1,
                  description:
                    'Partial index condition (WHERE) — raw SQL expression, e.g. "deleted_at IS NULL"',
                })
              ),
            },
            { additionalProperties: false }
          ),
        ]),
        {
          description: 'Additional database indexes to create (string or DBIndex object)',
        }
      )
    ),

    /** Foreign key relationships */
    foreignKeys: Type.Optional(
      Type.Array(ForeignKeySchema, {
        description: 'Foreign key relationships',
      })
    ),

    /** Table dependencies (for creation order) */
    dependencies: Type.Optional(
      Type.Array(Type.String(), {
        description: 'Table dependencies for creation order',
      })
    ),

    /**
     * Мягкое удаление — признак схемы (source of truth).
     * Живые строки = deleted_at IS NULL; deleteById() делает UPDATE deleted_at вместо DELETE.
     * Repository берёт дефолт отсюда; классовый override в наследнике побеждает.
     */
    softDelete: Type.Optional(
      Type.Boolean({
        description: 'Мягкое удаление: живые строки = deleted_at IS NULL',
      })
    ),

    /**
     * Полное имя таблицы в БД без префикса модуля.
     * Используется для доступа к legacy-таблицам (например, t_doc_imports).
     * Если задано — используется как итоговое имя вместо автоматического prefixed tableName.
     */
    rawTableName: Type.Optional(
      Type.String({
        pattern: '^[a-z][a-z0-9_]*$',
        description: 'Полное имя таблицы БД без префикса модуля (для legacy-таблиц)',
      })
    ),
  },
  {
    $id: 'SchemaDefinition',
    description: 'Schema definition structure for database table creation and validation',
  }
);

/**
 * SchemaDefinition — тип параметров для {@link defineSchema}.
 *
 * Содержит:
 * - schema: исходная TypeBox‑схема таблицы (TObject с AYA‑метаданными);
 * - tableName: базовое имя таблицы (без префикса модуля);
 * - additionalIndexes?: имена колонок для дополнительных индексов;
 * - foreignKeys?: явные внешние ключи (дополнят автогенерацию по RelationField);
 * - dependencies?: зависимые таблицы (порядок создания).
 *
 * Примечание: тип выведен из валидируемой на рантайме схемы {@link SchemaDefinitionSchema}.
 */
export type SchemaDefinition<TProps extends PropertiesMap = PropertiesMap> = Omit<
  Static<typeof SchemaDefinitionSchema>,
  'schema'
> & {
  schema: TObject<TProps>;
};

/**
 * ExtendedSchemaDefinition — расширенное описание схемы, возвращаемое {@link defineSchema}.
 *
 * Содержит:
 * - schema: TObject после обработки связей (RelationField остаются DB‑полями);
 * - tableName: итоговое имя таблицы (с модульным префиксом);
 * - createSchema / updateSchema: схемы для операций создания/обновления;
 * - additionalIndexes: объединённые индексы (явные + авто по FK);
 * - foreignKeys: объединённые внешние ключи (явные + авто по RelationField).
 */
export type ExtendedSchemaDefinition<T extends TObject<PropertiesMap> = TObject<PropertiesMap>> =
  (SchemaDefinition & {
    createSchema: FieldPropertiesSchema;
    updateSchema: FieldPropertiesSchema;
    schema: T;
  }) & {
    additionalIndexes: Array<string | DBIndex>;
    foreignKeys: ForeignKey[];
  };

/**
 * Process relations in schema and auto-generate FK fields and constraints
 * Now handles nullable option and auto-generates foreign key constraints
 */
function processRelations<TProps extends PropertiesMap>(schema: TObject<TProps>) {
  const processed: Record<string, TSchema> = { ...schema.properties };
  const autoForeignKeys: ForeignKey[] = [];

  for (const [fieldName, fieldDef] of Object.entries(schema.properties) as [
    string,
    AttributeSchema,
  ][]) {
    // Check for RelationField (FK fields) via safe narrowing
    if ('$isRelation' in fieldDef && fieldDef.$isRelation === true) {
      const rel = fieldDef;

      // Keep the field as is (it's already an IntegerField)
      processed[fieldName] = fieldDef;

      // Validate that RelationField has foreignKey metadata
      if (!rel.$foreignKey) {
        throw createValidationError(
          `RelationField '${fieldName}' must have $foreignKey metadata`,
          'foreignKey',
          fieldName,
          'RelationField'
        );
      }

      // Generate foreign key constraint from RelationField
      autoForeignKeys.push({
        column: fieldName,
        references: { table: rel.$foreignKey.table, column: rel.$foreignKey.column },
        onDelete: rel.$foreignKey.onDelete ?? 'RESTRICT',
        onUpdate: rel.$foreignKey.onUpdate ?? 'CASCADE',
      });
    }
    // RelationOne fields are kept as metadata only (no FK field creation)
  }

  // Return processed table schema and auto-generated foreign keys (no mutation)
  const tableSchema = Type.Object(processed);
  return [tableSchema, autoForeignKeys] as const;
}

/**
 * defineSchema — определяет схему таблицы платформы и формирует расширенные метаданные.
 *
 * Назначение:
 * - Валидирует входную TypeBox‑схему (TObject) на рантайме.
 * - Обрабатывает RelationField: автоматически создаёт внешние ключи (FK).
 * - Добавляет авто‑индексы по FK и объединяет их с {@link SchemaDefinition.additionalIndexes}.
 * - Автопрефиксирует имя таблицы по имени модуля (modules/<module>/...), если возможно определить по стеку.
 * - Генерирует схемы createSchema/updateSchema для форм/валидации на клиенте/сервере.
 *
 * Параметры:
 * @param definition Объект описания схемы:
 *  - schema: TObject со всеми полями таблицы (включая SystemFields и поля с метаданными AYA);
 *  - tableName: базовое имя таблицы без префикса модуля (будет дополнено автоматически);
 *  - additionalIndexes?: массив имён колонок для создания индексов;
 *  - foreignKeys?: явные внешние ключи (дополнит автогенерируемые по RelationField);
 *  - dependencies?: список таблиц‑зависимостей для порядка создания.
 *
 * Возвращает:
 * @returns ExtendedSchemaDefinition<T> с полями:
 *  - tableName: итоговое имя таблицы с модульным префиксом;
 *  - schema: TObject после обработки связей (RelationField сохранены как DB‑поля);
 *  - createSchema / updateSchema: схемы для создания/обновления записей;
 *  - additionalIndexes: полный список индексов (включая авто‑индексы по FK);
 *  - foreignKeys: полный список внешних ключей (авто + явные).
 *
 * Исключения:
 * - Бросает ValidationError при некорректной структуре schema, несуществующих колонках
 *   в additionalIndexes/foreignKeys или неверном формате определения.
 */
export function defineSchema<T extends TObject<PropertiesMap>>(
  definition: Omit<SchemaDefinition, 'schema'> & { schema: T }
): ExtendedSchemaDefinition<T> {
  // Validate that provided schema is a real TypeBox TObject at runtime
  if (!Value.Check(TypeBoxObjectSchema, definition.schema)) {
    throw createValidationError(
      'SchemaDefinition.schema must be a TypeBox TObject created via Type.Object(...)',
      'schema',
      definition.schema,
      'SchemaDefinition'
    );
  }
  // Process relations first
  const [tableSchema, autoForeignKeys] = processRelations(
    definition.schema as TObject<PropertiesMap>
  );

  // ✨ Validate: check for duplicate foreign keys (RelationField already creates FK, no need in explicit foreignKeys)
  const autoFkColumns = new Set(autoForeignKeys.map(fk => fk.column));
  const explicitForeignKeys = definition.foreignKeys ?? [];
  const duplicateFks: string[] = [];

  for (const explicitFk of explicitForeignKeys) {
    if (autoFkColumns.has(explicitFk.column)) {
      duplicateFks.push(explicitFk.column);
    }
  }

  if (duplicateFks.length > 0) {
    throw createValidationError(
      `Duplicate foreign key definitions detected. The following columns are already defined via RelationField and should not be in 'foreignKeys': ${duplicateFks.join(', ')}. ` +
        `Remove these columns from the 'foreignKeys' array in defineSchema() - RelationField automatically creates FK constraints.`,
      'foreignKeys',
      duplicateFks,
      'SchemaDefinition'
    );
  }

  // ✨ Merge auto-generated foreign keys with explicit ones (no duplicates now)
  const allForeignKeys = [...autoForeignKeys, ...explicitForeignKeys];

  // ✨ Auto-add indexes for FK columns (best practice for performance)
  const autoIndexes = autoForeignKeys.map(fk => fk.column);
  const allIndexes = [...new Set([...autoIndexes, ...(definition.additionalIndexes ?? [])])];

  // Validate that all FK columns exist in the table schema
  const tableColumns = new Set(Object.keys(tableSchema.properties || {}));
  for (const fk of allForeignKeys) {
    if (!tableColumns.has(fk.column)) {
      throw createValidationError(
        `Foreign key column '${fk.column}' is not defined in schema properties`,
        'foreignKeys',
        fk.column,
        'SchemaDefinition'
      );
    }
  }

  // Validate that all additionalIndexes fields exist in the table schema
  // Support string (single column) and DBIndex (composite/unique)
  const additionalIndexes = allIndexes as Array<string | DBIndex>;
  for (const indexDef of additionalIndexes) {
    if (typeof indexDef === 'string') {
      if (!tableColumns.has(indexDef)) {
        throw createValidationError(
          `Index field '${indexDef}' is not defined in schema properties`,
          'additionalIndexes',
          indexDef,
          'SchemaDefinition'
        );
      }
    } else {
      // DBIndex: validate all columns
      for (const col of indexDef.columns) {
        if (!tableColumns.has(col)) {
          throw createValidationError(
            `Index field '${col}' (in composite index) is not defined in schema properties`,
            'additionalIndexes',
            col,
            'SchemaDefinition'
          );
        }
      }
    }
  }

  // Create new definition with processed schema, foreign keys, and indexes
  const processedDefinition = {
    ...definition,
    schema: tableSchema,
    foreignKeys: allForeignKeys,
    additionalIndexes: additionalIndexes,
  };

  // Use improved validation with helpful error messages
  const isValid = Value.Check(SchemaDefinitionSchema, processedDefinition);

  if (!isValid) {
    const errors = [...Value.Errors(SchemaDefinitionSchema, processedDefinition)];
    const errorMessages = errors.map(
      error => `${error.path}: ${error.message} (got: ${JSON.stringify(error.value)})`
    );

    // Create helpful validation error
    throw createValidationError(
      `Schema definition validation failed:\n${errorMessages.join('\n')}`,
      errors[0]?.path,
      errors[0]?.value,
      'SchemaDefinition'
    );
  }

  // AUTO-PREFIX TABLE NAME
  // Источники имени модуля (по приоритету):
  //   1. rawTableName — итоговое имя задано явно, префикс не добавляем (legacy-таблицы).
  //   2. definition.moduleName — пробрасывается из `../module.json` каждой схемы:
  //
  //        import { name as moduleName } from '../module.json';
  //        export default defineSchema({ moduleName, tableName: 'agents', ... });
  //
  //      Детерминированный путь: работает и в dev (tsx), и в prod-bundle.
  //
  // Других источников нет. Раньше существовал fallback по stack-trace, но он
  // ломался в bundled prod (esbuild сохраняет path-as-function-name не для всех
  // модулей — зависит от lazy-init обёртки). Это маскировало баги: схема в dev
  // получала префикс через стек, в prod — нет, и таблица не находилась только
  // в проде. Теперь — fail loud при отсутствии moduleName.
  let finalDefinition = processedDefinition;
  if (definition.rawTableName) {
    finalDefinition = {
      ...processedDefinition,
      tableName: definition.rawTableName,
    };
  } else if (definition.moduleName) {
    const { tableName: originalTableName } = processedDefinition;
    finalDefinition = {
      ...processedDefinition,
      tableName: createTableName(definition.moduleName, originalTableName),
    };
  } else {
    throw createValidationError(
      `Schema "${processedDefinition.tableName}": не передан moduleName и rawTableName. ` +
        `Добавьте в начало файла:\n` +
        `  import { name as moduleName } from '../module.json';\n` +
        `и пробросьте moduleName в defineSchema({ moduleName, ... }).\n` +
        `Либо используйте rawTableName для legacy-таблиц без префикса.`,
      'moduleName',
      undefined,
      'SchemaDefinition'
    );
  }

  // Exclude RelationOne fields (no $fieldType) and system fields from create/update schemas
  // Include RelationField fields (have $fieldType even with $isRelation)
  const SYSTEM_FIELDS = Object.keys(SystemFields);
  // Базовые проперти таблицы с сохранением AYA-метаданных
  const baseProps = tableSchema.properties as FieldProperties;
  const createFields: FieldProperties = {};
  for (const [key, value] of Object.entries(baseProps)) {
    const field = value as Partial<FieldMeta>;
    // Include field if it has $fieldType (real DB field) and not a system field
    // RelationOne fields don't have $fieldType, RelationField fields do
    if (field.$fieldType && !SYSTEM_FIELDS.includes(key)) {
      createFields[key] = value;
    }
  }

  //TODO: можем вернуть явные type путем преобразования типов
  // Формируем createSchema как TObject с метаданными
  const createSchema: FieldPropertiesSchema = Type.Object(createFields);
  // updateSchema: id is required, all other createFields are optional
  // const updateFields: Record<string, any> = { id: baseProps.id };
  const updateFields: FieldProperties = {}; // убираем id как обязательное
  for (const [key, value] of Object.entries(createFields)) {
    // Сохраняем метаданные поля при оборачивании в Optional
    const optional = Type.Optional(value) as TSchema & Partial<FieldMeta>;
    const meta = value as Partial<FieldMeta>;
    if (meta.$fieldType !== undefined)
      (optional as Record<string, unknown>).$fieldType = meta.$fieldType;
    updateFields[key] = optional;
  }
  const updateSchema: FieldPropertiesSchema = Type.Object(updateFields);

  // Регистрируем приватные ($private) поля схемы в глобальном реестре —
  // egress-страховка на RPC-выходе (см. private-fields-registry.ts).
  const privateNames = Object.entries(baseProps)
    .filter(([, value]) => (value as Partial<FieldMeta>).$private === true)
    .map(([key]) => key);
  if (privateNames.length > 0) {
    registerPrivateFieldNames(privateNames);
  }

  // Return enhanced definition with form schemas
  return {
    ...finalDefinition,
    createSchema,
    updateSchema,
    schema: definition.schema as T, // ✅ Preserve concrete type T
  };
}

// ============================================================================
// LEDGER SCHEMA
// ============================================================================

/**
 * LedgerSchemaDefinition — расширенный тип схемы для ledger-репозиториев.
 *
 * Несёт compile-time информацию о ролях полей через phantom types.
 * Runtime-доступные поля: ledgerDimensions, ledgerMeasures, ledgerSources.
 */
export interface LedgerSchemaDefinition<
  T,
  DKeys extends keyof T,
  MKeys extends keyof T,
  SKeys extends keyof T,
> extends ExtendedSchemaDefinition {
  /** Phantom field — compile-time only, не существует в runtime */
  readonly __ledgerTypes?: {
    entry: T;
    dimensions: Pick<T, DKeys>;
    measures: Pick<T, MKeys>;
    sources: Pick<T, SKeys>;
  };
  /** Runtime: имена полей-измерений */
  readonly ledgerDimensions: readonly string[];
  /** Runtime: имена полей-мер */
  readonly ledgerMeasures: readonly string[];
  /** Runtime: имена полей-источников */
  readonly ledgerSources: readonly string[];
}

/** Извлекает тип полной записи из LedgerSchemaDefinition */
export type InferLedgerEntry<S> =
  S extends LedgerSchemaDefinition<infer T, infer _D, infer _M, infer _S> ? T : never;
/** Извлекает тип dimensions (Pick) из LedgerSchemaDefinition */
export type InferLedgerDimensions<S> =
  S extends LedgerSchemaDefinition<infer T, infer D, infer _M, infer _S> ? Pick<T, D> : never;
/** Извлекает тип measures (Pick) из LedgerSchemaDefinition */
export type InferLedgerMeasures<S> =
  S extends LedgerSchemaDefinition<infer T, infer _D, infer M, infer _S> ? Pick<T, M> : never;
/** Извлекает тип sources (Pick) из LedgerSchemaDefinition */
export type InferLedgerSources<S> =
  S extends LedgerSchemaDefinition<infer T, infer _D, infer _M, infer S2> ? Pick<T, S2> : never;

/** Системные поля, которые не являются attributes */
type SystemFieldKeys =
  | 'id'
  | 'uuid'
  | 'created_at'
  | 'updated_at'
  | 'sort_order'
  | 'created_by_id'
  | 'updated_by_id'
  | 'deleted_at'
  | 'deleted_by_id';

/** Извлекает тип attributes — все поля кроме dimensions, measures, sources, system, period */
export type InferLedgerAttributes<S> =
  S extends LedgerSchemaDefinition<infer T, infer D, infer M, infer S2>
    ? Omit<T, D | M | S2 | SystemFieldKeys | 'period'>
    : never;

/**
 * defineLedgerSchema — декларативное определение ledger-схемы.
 *
 * Тонкая обёртка над defineSchema(). Разработчик описывает поля в стандартном
 * формате Type.Object({ ...SystemFields, ... }), а дополнительно тремя массивами
 * задаёт роли полей: dimensions, measures, sources.
 *
 * @param config.tableName — базовое имя таблицы (без префикса модуля)
 * @param config.schema — стандартная TypeBox-схема с ...SystemFields и period
 * @param config.dimensions — as const массив имён полей-измерений (GROUP BY)
 * @param config.measures — as const массив имён полей-мер (SUM)
 * @param config.sources — as const массив имён полей привязки к источнику
 * @param config.additionalIndexes — дополнительные индексы сверх автогенерации
 */
export function defineLedgerSchema<
  T extends TObject<TProperties>,
  D extends readonly (keyof Static<T>)[],
  M extends readonly (keyof Static<T>)[],
  S extends readonly (keyof Static<T>)[],
>(config: {
  tableName: string;
  schema: T;
  dimensions: D;
  measures: M;
  sources: S;
  additionalIndexes?: Array<string | DBIndex>;
  /**
   * Имя модуля для авто-префикса tableName. Передаётся как
   * `import { name as moduleName } from '../module.json'` — детерминированно
   * работает в любом окружении (dev/tsx/prod-bundle), не зависит от стек-трейса.
   */
  moduleName?: string;
}): LedgerSchemaDefinition<Static<T> & { period: string | Date }, D[number], M[number], S[number]> {
  const properties = config.schema.properties as Record<string, Record<string, unknown>>;
  const systemFieldNames = new Set(getSystemFields());

  // --- Инжект системного поля period ---

  if (!properties['period']) {
    const periodDef = TimestampField({ title: 'Период' });
    properties['period'] = periodDef as unknown as Record<string, unknown>;
  }

  // --- Валидация ---

  if (config.dimensions.length === 0) {
    throw createValidationError(
      'Ledger schema must have at least one dimension',
      'dimensions',
      config.dimensions,
      'LedgerSchemaDefinition'
    );
  }

  if (config.measures.length === 0) {
    throw createValidationError(
      'Ledger schema must have at least one measure',
      'measures',
      config.measures,
      'LedgerSchemaDefinition'
    );
  }

  if (config.sources.length === 0) {
    throw createValidationError(
      'Ledger schema must have at least one source',
      'sources',
      config.sources,
      'LedgerSchemaDefinition'
    );
  }

  // Проверяем пересечения между dimensions, measures, sources
  const allRoleFields = [
    ...config.dimensions.map(String),
    ...config.measures.map(String),
    ...config.sources.map(String),
  ];
  const seen = new Set<string>();
  for (const name of allRoleFields) {
    if (seen.has(name)) {
      throw createValidationError(
        `Field "${name}" appears in multiple role arrays (dimensions/measures/sources). Each field can have only one role`,
        'dimensions/measures/sources',
        name,
        'LedgerSchemaDefinition'
      );
    }
    seen.add(name);
  }

  // Проверяем каждый dimension
  for (const dim of config.dimensions) {
    const fieldName = String(dim);
    if (!properties[fieldName]) {
      throw createValidationError(
        `Dimension field "${fieldName}" does not exist in schema`,
        'dimensions',
        fieldName,
        'LedgerSchemaDefinition'
      );
    }
    if (systemFieldNames.has(fieldName)) {
      throw createValidationError(
        `Dimension field "${fieldName}" is a system field and cannot be used as a dimension`,
        'dimensions',
        fieldName,
        'LedgerSchemaDefinition'
      );
    }
  }

  // Проверяем каждый measure
  for (const m of config.measures) {
    const fieldName = String(m);
    const field = properties[fieldName];
    if (!field) {
      throw createValidationError(
        `Measure field "${fieldName}" does not exist in schema`,
        'measures',
        fieldName,
        'LedgerSchemaDefinition'
      );
    }
    if (field.$fieldType !== 'decimal') {
      throw createValidationError(
        `Measure field "${fieldName}" must be a DecimalField ($fieldType = "decimal"), got "${String(field.$fieldType)}"`,
        'measures',
        fieldName,
        'LedgerSchemaDefinition'
      );
    }
    if (systemFieldNames.has(fieldName)) {
      throw createValidationError(
        `Measure field "${fieldName}" is a system field and cannot be used as a measure`,
        'measures',
        fieldName,
        'LedgerSchemaDefinition'
      );
    }
  }

  // Проверяем каждый source
  for (const s of config.sources) {
    const fieldName = String(s);
    const field = properties[fieldName];
    if (!field) {
      throw createValidationError(
        `Source field "${fieldName}" does not exist in schema`,
        'sources',
        fieldName,
        'LedgerSchemaDefinition'
      );
    }
    if (field.$nullable === true) {
      throw createValidationError(
        `Source field "${fieldName}" cannot be nullable — voidBySource() with null would delete all records`,
        'sources',
        fieldName,
        'LedgerSchemaDefinition'
      );
    }
    if (systemFieldNames.has(fieldName)) {
      throw createValidationError(
        `Source field "${fieldName}" is a system field and cannot be used as a source`,
        'sources',
        fieldName,
        'LedgerSchemaDefinition'
      );
    }
  }

  // --- Пометка полей $ledgerRole ---

  const taggedFields = new Set(allRoleFields);
  for (const dim of config.dimensions) {
    properties[String(dim)].$ledgerRole = 'dimension';
  }
  for (const m of config.measures) {
    properties[String(m)].$ledgerRole = 'measure';
  }
  for (const s of config.sources) {
    properties[String(s)].$ledgerRole = 'source';
  }
  for (const [fieldName, fieldDef] of Object.entries(properties)) {
    if (
      !systemFieldNames.has(fieldName) &&
      !taggedFields.has(fieldName) &&
      fieldName !== 'period'
    ) {
      fieldDef.$ledgerRole = 'attribute';
    }
  }

  // --- Автогенерация индексов ---

  // Имена индексов — от ПОЛНОГО физического имени таблицы (module_table):
  // зона авто-удаления schema-sync — `idx_{физическое_имя}_*`, имена от короткого
  // tableName в неё не попадают, и при смене канона старые индексы осиротели бы
  const tbl = config.moduleName
    ? createTableName(config.moduleName, config.tableName)
    : config.tableName;
  const measureColumns = config.measures.map(String);
  // Индексы по dimensions — covering (INCLUDE measures): агрегаты balance()/
  // sumExpression() отвечают index-only scan'ом без обращения к heap.
  // Замер на 50 млн строк: групповой balance по складу 13.9с → 0.47с (30x),
  // sumExpression по одному dimension 314мс → 1.1мс (~300x).
  // Пер-dimension индексы нужны для НЕ-ведущих dimensions: составной работает
  // только с префикса, а подзапросы sumExpression()/withColumn фильтруют по одному.
  const perDimensionIndexes: DBIndex[] = config.dimensions.slice(1).map(d => ({
    columns: [String(d)],
    type: 'btree',
    include: measureColumns,
    name: `idx_${tbl}_dim_${String(d)}_cov`,
  }));
  const autoIndexes: DBIndex[] = [
    {
      columns: config.dimensions.map(String),
      type: 'btree',
      include: measureColumns,
      name: `idx_${tbl}_dims_cov`,
    },
    ...perDimensionIndexes,
    { columns: ['period'], type: 'btree', name: `idx_${tbl}_period` },
    { columns: ['created_at'], type: 'brin', name: `idx_${tbl}_created_brin` },
    { columns: config.sources.map(String), type: 'btree', name: `idx_${tbl}_sources` },
  ];
  const mergedIndexes: Array<string | DBIndex> = [
    ...autoIndexes,
    ...(config.additionalIndexes ?? []),
  ];

  // --- Вызов defineSchema() ---

  const extendedSchema = defineSchema({
    tableName: config.tableName,
    schema: config.schema,
    additionalIndexes: mergedIndexes,
    moduleName: config.moduleName,
  });

  // --- Добавляем ledger-метаданные к результату (после defineSchema, чтобы не сломать SchemaDefinitionSchema) ---

  const result = extendedSchema as unknown as LedgerSchemaDefinition<
    Static<T> & { period: string | Date },
    D[number],
    M[number],
    S[number]
  >;
  Object.defineProperty(result, 'ledgerDimensions', {
    value: Object.freeze([...config.dimensions.map(String)]),
    writable: false,
    enumerable: true,
  });
  Object.defineProperty(result, 'ledgerMeasures', {
    value: Object.freeze([...config.measures.map(String)]),
    writable: false,
    enumerable: true,
  });
  Object.defineProperty(result, 'ledgerSources', {
    value: Object.freeze([...config.sources.map(String)]),
    writable: false,
    enumerable: true,
  });
  return result;
}
